import {
  ShippingMethodEnum,
  ShippingOptionUnsupportedReasonEnum,
  GetProductUsShippingDetailsQuery_getProductShippingDetailsV2_ProductShippingDetailsV2_shippingOptions_ProductShippingOptionV2 as UsShippingOption,
} from "@/Apollo/schema";

/**
 * Why a US-shippable listing gets no US postal rate, as the buyer reads it:
 *
 * - `countryUnsupported`: we don't deliver this item to the US (WHO-6640), for instance while US
 *   checkout is closed.
 * - `originUnsupported`: the seller's country can't send a parcel to the US yet (Sendcloud labels
 *   NL → US only today, WHO-6587).
 * - `quoteUnavailable`: the carrier gave no rate this time; it may on another visit.
 * - `notByPost`: the parcel itself can't go (size, weight, value, missing dimensions).
 */
export type UsPostalUnsupported =
  | "countryUnsupported"
  | "originUnsupported"
  | "quoteUnavailable"
  | "notByPost";

/**
 * Partial on purpose (Andrew, 5 Nov), unlike the repo's usual exhaustive `Record` over a generated
 * enum: a reason the gateway adds later doesn't fail the type check, it reads as `notByPost` until
 * it is mapped here.
 */
const UNSUPPORTED_BY_REASON: Partial<
  Record<ShippingOptionUnsupportedReasonEnum, UsPostalUnsupported>
> = {
  [ShippingOptionUnsupportedReasonEnum.DeliveryCountryUnsupported]:
    "countryUnsupported",
  [ShippingOptionUnsupportedReasonEnum.PickupCountryUnsupported]:
    "originUnsupported",
  [ShippingOptionUnsupportedReasonEnum.QuoteUnavailable]: "quoteUnavailable",
  [ShippingOptionUnsupportedReasonEnum.QuoteInfeasible]: "notByPost",
  [ShippingOptionUnsupportedReasonEnum.DimensionsExceedMax]: "notByPost",
  [ShippingOptionUnsupportedReasonEnum.MissingDimensions]: "notByPost",
  [ShippingOptionUnsupportedReasonEnum.PriceExceedsMax]: "notByPost",
  [ShippingOptionUnsupportedReasonEnum.MerchantDisabled]: "notByPost",
};

export type UsPostalView =
  | {
      kind: "rate";
      price: NonNullable<UsShippingOption["price"]>;
    }
  | { kind: "unsupported"; reason: UsPostalUnsupported };

/**
 * WHO-6509. The US delivery a postal listing gets on `/en-us`, from the gateway's options for a US
 * address with the unsupported ones included. Pickup is left out: it is in the seller's country,
 * not a delivery to the US. Only a postal option gives a rate, since the page labels it "by post";
 * any other delivery option is read only for why it can't go. No delivery option at all means the
 * gateway delivers nothing of this listing to the US; no answer at all says nothing yet.
 */
export const getUsPostalView = (
  options: readonly UsShippingOption[] | null | undefined
): UsPostalView => {
  if (!options) {
    return { kind: "unsupported", reason: "quoteUnavailable" };
  }

  const delivery =
    options.find(
      option => option.shippingMethod === ShippingMethodEnum.Postal
    ) ??
    options.find(option => option.shippingMethod !== ShippingMethodEnum.Pickup);

  if (!delivery) {
    return { kind: "unsupported", reason: "countryUnsupported" };
  }

  if (!delivery.isSupported) {
    return {
      kind: "unsupported",
      /* A reason this map doesn't name yet still says why: not by post. */
      reason:
        (delivery.unsupportedReason &&
          UNSUPPORTED_BY_REASON[delivery.unsupportedReason]) ??
        "notByPost",
    };
  }

  if (delivery.shippingMethod !== ShippingMethodEnum.Postal) {
    return { kind: "unsupported", reason: "notByPost" };
  }

  if (!delivery.price) {
    return { kind: "unsupported", reason: "quoteUnavailable" };
  }

  return { kind: "rate", price: delivery.price };
};

/**
 * Whether "Delivery in 4–8 weeks" and the duties note still apply: not when the item can't be
 * sent to the US at all. A rate that may come on another visit keeps them.
 */
export const showsUsDeliveryFacts = (view: UsPostalView): boolean =>
  view.kind === "rate" || view.reason === "quoteUnavailable";
